import React, { Component } from 'react';
import { Link,Navigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default class Part1 extends Component {

    render() {

        var userToken = localStorage.getItem('token');
        if (!userToken) {
            return <Navigate to='/login' replace="true" />
        }

        const canonicalExample = JSON.stringify({
            "relationship": "FATHER",
            "purpose": "EDUCATION",
            "rail": "BANK_ACCOUNT",
            "amount_src": 1000,
            "src_currency": "USD",
            "dst_currency": "INR"
        }, null, 2);

        const copyClipboard = (e, data) => {
            e.preventDefault();
            navigator.clipboard.writeText(data);
            toast.success('Copied to clipboard', {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 2000
            });
        };

        return (
            <div className="docs-content">
                <div className="container">

                    {/* ========================= */}
                    {/* 1. PSP SECTION */}
                    {/* ========================= */}

                    <article className="docs-article mb-5">
                        <h1 className="docs-heading mb-4">
                            1. Payment Service Provider (PSP)
                        </h1>

                        <div className="alert alert-info">
                            A PSP is any external entity that executes, facilitates,
                            clears, settles, or intermediates a financial transaction
                            on behalf of a platform.
                        </div>

                        <h5 className="mt-4">Examples</h5>
                        <ul className="mt-2 doc-page">
                            <li>Card acquirers — <strong>Adyen</strong></li>
                            <li>Remittance networks — <strong>TerraPay, Tranglo</strong></li>
                            <li>Wallet aggregators — <strong>Nsano</strong></li>
                            <li>Middleware aggregators — <strong>MSF</strong></li>
                            <li>Crypto rails / exchanges — <strong>Shift</strong></li>
                        </ul>

                        <h5 className="mt-4">A PSP is NOT just an API</h5>

                        <div className="card mt-3 p-3 bg-light border-0">
                            <ul className="mb-0 doc-page">
                                <li><strong>Legal permissions</strong></li>
                                <li><strong>Operational behavior</strong></li>
                                <li><strong>Technical interfaces</strong></li>
                                <li><strong>Compliance expectations</strong></li>
                                <li><strong>Failure modes</strong></li>
                            </ul>
                        </div>

                        <div className="alert alert-warning mt-4">
                            Treat each PSP as a <strong>non-deterministic external system</strong>.
                            Never assume symmetry across PSPs.
                        </div>

                    </article>


                    {/* ========================= */}
                    {/* 2. CANONICAL MODEL */}
                    {/* ========================= */}

                    <article className="docs-article mb-5">
                        <h1 className="docs-heading mb-4">
                            2. Canonical Model / Canonical Contract
                        </h1>

                        <p>
                            A canonical model is a PSP-agnostic, internally consistent
                            representation of transactions, parties, compliance data,
                            and lifecycle states.
                        </p>

                        <div className="row mt-4">
                            <div className="col-md-6">
                                <div className="card p-3 border-success">
                                    <h6 className="text-success">What It Does</h6>
                                    <ul className="mb-0">
                                        <li>Single source of truth</li>
                                        <li>Decouples business logic from provider quirks</li>
                                        <li>Enables comparison & routing</li>
                                        <li>Improves auditability</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="card p-3 border-danger">
                                    <h6 className="text-danger">What It Is NOT</h6>
                                    <ul className="mb-0">
                                        <li>Not a mirror of any single PSP</li>
                                        <li>Not a lowest common denominator</li>
                                        <li>Not UI-specific</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <h5 className="mt-5">Example Canonical Transaction</h5>

                        <div className="copybox mt-3">
                            <div className="copybox-top d-flex justify-content-between align-items-center">
                                <p className="copybox-para mb-0">Canonical JSON</p>
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={(e) => copyClipboard(e, canonicalExample)}
                                >
                                    Copy
                                </button>
                            </div>
                            <div className="copybox-bottom">
                                <pre className="mb-0">
                                    <code className="copybox-code">
                                        {canonicalExample}
                                    </code>
                                </pre>
                            </div>
                        </div>

                        <div className="alert alert-secondary mt-4">
                            If two PSPs require different fields for the same transaction,
                            the canonical model must contain both —
                            even if one PSP ignores them.
                        </div>

                    </article>


                    {/* ========================= */}
                    {/* 3. CAPABILITY MATRIX */}
                    {/* ========================= */}

                    <article className="docs-article mb-5">
                        <h1 className="docs-heading mb-4">
                            3. Capability Matrix
                        </h1>

                        <p>
                            A capability matrix is a structured comparison framework
                            describing what each PSP:
                        </p>

                        <div className="card p-3 bg-light border-0 mt-3">
                            <div className="row text-center doc-page">
                                <div className="col-md-3"><strong>Supports</strong></div>
                                <div className="col-md-3"><strong>Partially Supports</strong></div>
                                <div className="col-md-3"><strong>Does Not Support</strong></div>
                                <div className="col-md-3"><strong>Implicit Support</strong></div>
                            </div>
                        </div>

                        <h5 className="mt-5">Capability Dimensions</h5>

                        <div className="row mt-3">
                            {[
                                "Intents",
                                "Rails",
                                "Countries",
                                "Authentication",
                                "Compliance",
                                "Async Behavior",
                                "Settlement",
                                "Failure Handling"
                            ].map((item, index) => (
                                <div className="col-md-3 mb-3" key={index}>
                                    <div className="card text-center p-3 h-100">
                                        {item}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <h5 className="mt-5">Why It Exists</h5>

                        <div className="alert alert-success mt-3">
                            <ul className="mb-0">
                                <li>Prevents tribal knowledge</li>
                                <li>Enables deterministic routing</li>
                                <li>Supports regulator & partner due diligence</li>
                            </ul>
                        </div>

                        <div className="alert alert-dark mt-4">
                            The matrix must be:
                            <strong> machine-readable, versioned, and tied to adapter code.</strong>
                        </div>

                    </article>
                    
                    <article class="docs-article" id="section-3">
                        <section class="docs-section row" id="item-3-1">
                            <div class="col-12">
                                <nav aria-label="Page navigation example">
                                    <ul class="pagination justify-content-between">
                                        <li class="page-item">
                                        </li>
                                        <li class="page-item">
                                            <Link class="page-link" to="/part-two-doc">
                                                Part II &nbsp; <i class="bi bi-arrow-right"></i>
                                            </Link>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        </section>
                    </article>

                </div>

                <ToastContainer closeOnClick autoClose={2000} />
            </div>
        )
    }
}